import React, { useCallback, useEffect, useRef, useState } from 'react'
import type { PortalGuest, PortalHotel, PortalRoom } from '@/lib/types'
import { Badge, Button, Input, Modal, OccupancyBadge, SaveState, SaveStatus, useToast } from '@/components/ui'
import { nightsBetween, roomOccupancyStatus, totalNights } from '@/lib/domain/roomLogic'
import { formatDateOnly } from '@/lib/format'
import { tryParseJson } from '@/lib/supabaseClient'

// Suggested board types (meal plans) offered via a <datalist> on the Board
// Type field -- admins can still type any custom value, this is just so
// nobody has to remember the standard hospitality abbreviations by heart.
const BOARD_TYPE_SUGGESTIONS = ['Room Only', 'Bed & Breakfast', 'Half Board', 'Full Board', 'All Inclusive']

export interface GuestWriteResult {
  ok: boolean
  code?: string
  detail?: string | null
  updated?: { updated_at: string }
}

// Each room type gets a consistent color from this palette (picked by its position
// within the hotel), so the page reads as more than black-and-white and different
// room types (Single, Double, Triple, …) are easy to tell apart at a glance while
// scrolling a long, single-column list of room cards.
const TYPE_PALETTE = [
  { header: 'bg-slate-100 border-slate-300', dot: 'bg-slate-700', label: 'text-slate-900', card: 'border-l-slate-400' },
  { header: 'bg-blue-50 border-blue-200', dot: 'bg-blue-600', label: 'text-blue-800', card: 'border-l-blue-400' },
  { header: 'bg-emerald-50 border-emerald-200', dot: 'bg-emerald-600', label: 'text-emerald-800', card: 'border-l-emerald-400' },
  { header: 'bg-amber-50 border-amber-200', dot: 'bg-amber-600', label: 'text-amber-800', card: 'border-l-amber-400' },
  { header: 'bg-purple-50 border-purple-200', dot: 'bg-purple-600', label: 'text-purple-800', card: 'border-l-purple-400' },
  { header: 'bg-rose-50 border-rose-200', dot: 'bg-rose-600', label: 'text-rose-800', card: 'border-l-rose-400' },
  { header: 'bg-cyan-50 border-cyan-200', dot: 'bg-cyan-600', label: 'text-cyan-800', card: 'border-l-cyan-400' },
  { header: 'bg-fuchsia-50 border-fuchsia-200', dot: 'bg-fuchsia-600', label: 'text-fuchsia-800', card: 'border-l-fuchsia-400' },
] as const
type TypeColor = { header: string; dot: string; label: string; card: string }

// Dark/gold "brand" variant of the same palette, used on the company-facing
// portal page (see CompanyPortal.tsx's `dark` prop) so it matches the black +
// gold event-brand theme instead of the light admin theme.
const TYPE_PALETTE_DARK = [
  { header: 'bg-amber-500/10 border-amber-500/30', dot: 'bg-amber-400', label: 'text-amber-300', card: 'border-l-amber-500' },
  { header: 'bg-sky-500/10 border-sky-500/30', dot: 'bg-sky-400', label: 'text-sky-300', card: 'border-l-sky-500' },
  { header: 'bg-emerald-500/10 border-emerald-500/30', dot: 'bg-emerald-400', label: 'text-emerald-300', card: 'border-l-emerald-500' },
  { header: 'bg-rose-500/10 border-rose-500/30', dot: 'bg-rose-400', label: 'text-rose-300', card: 'border-l-rose-500' },
  { header: 'bg-violet-500/10 border-violet-500/30', dot: 'bg-violet-400', label: 'text-violet-300', card: 'border-l-violet-500' },
  { header: 'bg-cyan-500/10 border-cyan-500/30', dot: 'bg-cyan-400', label: 'text-cyan-300', card: 'border-l-cyan-500' },
  { header: 'bg-fuchsia-500/10 border-fuchsia-500/30', dot: 'bg-fuchsia-400', label: 'text-fuchsia-300', card: 'border-l-fuchsia-500' },
  { header: 'bg-lime-500/10 border-lime-500/30', dot: 'bg-lime-400', label: 'text-lime-300', card: 'border-l-lime-500' },
] as const

export interface RoomTreeProps {
  hotels: PortalHotel[]
  showPhysicalRoomNumbers: boolean
  isAdmin: boolean
  canFill: boolean
  canEdit: boolean
  canClear: boolean
  canMove: boolean
  readOnlyMessage?: string
  /** Black + gold brand theme (used on the company portal). Defaults to the light admin theme. */
  dark?: boolean
  onSaveGuest: (guestId: string, fields: { full_name: string; mobile: string; email?: string | null }, expectedUpdatedAt: string) => Promise<GuestWriteResult>
  onClearGuest: (guestId: string, expectedUpdatedAt: string) => Promise<GuestWriteResult>
  onMoveGuest: (fromGuestId: string, toGuestId: string) => Promise<GuestWriteResult>
  onSaveRoom?: (
    roomId: string,
    fields: { physical_room_number: string | null; check_in: string | null; check_out: string | null; board_type: string | null }
  ) => Promise<GuestWriteResult>
  /** Adds one extra empty guest slot to a room whose allocation has unlimited_capacity. */
  onAddGuestSlot?: (roomId: string) => Promise<GuestWriteResult>
  /** Removes a self-added, still-empty room. */
  onRemoveRoom?: (roomId: string) => Promise<GuestWriteResult>
  onDataChanged: () => void
}

export default function RoomTree(props: RoomTreeProps) {
  const { hotels, dark = false, isAdmin } = props
  const [openHotels, setOpenHotels] = useState<Set<string>>(new Set(hotels.map((h) => h.id)))

  if (hotels.length === 0) {
    return (
      <p className={`rounded border border-dashed p-6 text-center text-sm ${dark ? 'border-neutral-700 text-neutral-500' : 'border-slate-300 text-slate-500'}`}>
        No rooms allocated yet.
      </p>
    )
  }

  return (
    <div className="space-y-6">
      {hotels.map((hotel) => {
        const isOpen = openHotels.has(hotel.id)
        const roomCount = hotel.room_types.reduce((s, rt) => s + rt.rooms.length, 0)
        const filledCount = hotel.room_types.reduce((s, rt) => s + rt.rooms.reduce((s2, r) => s2 + r.guests.filter((g) => !!g.full_name).length, 0), 0)
        const capacityCount = hotel.room_types.reduce((s, rt) => s + rt.rooms.reduce((s2, r) => s2 + r.guests.length, 0), 0)
        const hotelTotalNights = isAdmin ? totalNights([hotel]) : 0
        return (
          <div
            key={hotel.id}
            className={`overflow-hidden rounded-xl border shadow-sm ${dark ? 'border-amber-900/30 bg-neutral-950' : 'border-slate-200 bg-white'}`}
          >
            <button
              className={`flex w-full items-center justify-between gap-3 px-5 py-4 text-left transition-colors ${
                dark ? 'bg-black hover:bg-neutral-900' : 'bg-slate-900 hover:bg-slate-800'
              }`}
              onClick={() =>
                setOpenHotels((s) => {
                  const next = new Set(s)
                  if (next.has(hotel.id)) next.delete(hotel.id)
                  else next.add(hotel.id)
                  return next
                })
              }
            >
              <div className="flex items-center gap-3">
                <span className={`flex h-9 w-9 items-center justify-center rounded-lg text-lg ${dark ? 'bg-amber-500/15' : 'bg-white/10'}`}>🏨</span>
                <div>
                  <div className={`text-base font-semibold tracking-tight ${dark ? 'text-amber-50' : 'text-white'}`}>{hotel.name}</div>
                  <div className={`text-xs ${dark ? 'text-amber-200/50' : 'text-slate-300'}`}>
                    {roomCount} room{roomCount === 1 ? '' : 's'} · {filledCount}/{capacityCount} guests entered
                    {isAdmin && hotelTotalNights > 0 && ` · Total nights: ${hotelTotalNights}`}
                  </div>
                </div>
              </div>
              <span className={`text-sm ${dark ? 'text-amber-200/50' : 'text-slate-300'}`}>{isOpen ? '▲' : '▼'}</span>
            </button>
            {isOpen && (
              <div className={`space-y-6 px-5 py-5 ${dark ? 'bg-black' : 'bg-slate-50/60'}`}>
                {hotel.room_types.map((rt, rtIdx) => {
                  const palette = dark ? TYPE_PALETTE_DARK : TYPE_PALETTE
                  const typeColor = palette[rtIdx % palette.length]
                  return (
                    <div key={rt.id}>
                      <div className={`mb-4 flex items-center gap-3 rounded-lg border px-4 py-3 ${typeColor.header}`}>
                        <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${typeColor.dot}`} />
                        <h4 className={`text-base font-bold uppercase tracking-wide ${typeColor.label}`}>{rt.name}</h4>
                        <span className={`text-xs font-medium ${dark ? 'text-neutral-500' : 'text-slate-500'}`}>
                          {rt.rooms.length} room{rt.rooms.length === 1 ? '' : 's'}
                        </span>
                      </div>
                      <div className="space-y-4">
                        {rt.rooms.map((room) => (
                          <RoomCard key={room.id} room={room} allHotels={hotels} typeColor={typeColor} {...props} dark={dark} />
                        ))}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

function RoomCard({
  room,
  allHotels,
  typeColor,
  showPhysicalRoomNumbers,
  isAdmin,
  canFill,
  canEdit,
  canClear,
  canMove,
  readOnlyMessage,
  dark = false,
  onSaveGuest,
  onClearGuest,
  onMoveGuest,
  onSaveRoom,
  onAddGuestSlot,
  onRemoveRoom,
  onDataChanged,
}: RoomTreeProps & { room: PortalRoom; allHotels: PortalHotel[]; typeColor: TypeColor }) {
  const status = roomOccupancyStatus(room)
  const [roomActionBusy, setRoomActionBusy] = useState(false)
  const { push: pushRoomToast } = useToast()
  const isEmptyRoom = room.guests.every((g) => !g.full_name)

  async function addGuestSlot() {
    if (!onAddGuestSlot) return
    setRoomActionBusy(true)
    const res = await onAddGuestSlot(room.id)
    setRoomActionBusy(false)
    if (res.ok) {
      onDataChanged()
    } else {
      pushRoomToast(res.detail || res.code || 'Could not add a guest slot', 'error')
    }
  }

  async function removeRoom() {
    if (!onRemoveRoom) return
    setRoomActionBusy(true)
    const res = await onRemoveRoom(room.id)
    setRoomActionBusy(false)
    if (res.ok) {
      pushRoomToast('Room removed')
      onDataChanged()
    } else {
      pushRoomToast(res.detail || res.code || 'Could not remove this room', 'error')
    }
  }

  // A single combined draft + debounce for all four room-detail fields:
  // admin_update_room replaces the whole row every call, so every save must
  // resend the *current* value of all four together -- editing one field
  // right after another (e.g. check-in then board type) with separate
  // per-field debounces would otherwise race and stomp on each other with
  // stale values.
  const [draft, setDraft] = useState({
    physical_room_number: room.physical_room_number || '',
    check_in: room.check_in || '',
    check_out: room.check_out || '',
    board_type: room.board_type || '',
  })
  const [detailsSaveState, setDetailsSaveState] = useState<SaveState>('idle')
  const detailsTimer = useRef<ReturnType<typeof setTimeout>>()
  const nights = nightsBetween(draft.check_in || null, draft.check_out || null)

  function scheduleDetailsSave(next: Partial<typeof draft>) {
    const merged = { ...draft, ...next }
    setDraft(merged)
    if (!onSaveRoom) return
    setDetailsSaveState('saving')
    if (detailsTimer.current) clearTimeout(detailsTimer.current)
    detailsTimer.current = setTimeout(async () => {
      const res = await onSaveRoom(room.id, {
        physical_room_number: merged.physical_room_number || null,
        check_in: merged.check_in || null,
        check_out: merged.check_out || null,
        board_type: merged.board_type || null,
      })
      setDetailsSaveState(res.ok ? 'saved' : 'error')
      if (res.ok) onDataChanged()
    }, 600)
  }

  const canEditDetails = isAdmin && !!onSaveRoom
  const hasAnyDetail = room.physical_room_number || room.check_in || room.check_out || room.board_type
  const inputDark = dark ? '!border-neutral-700 !bg-neutral-900 !text-neutral-100 placeholder:!text-neutral-600 focus:!border-amber-500 focus:!ring-amber-500' : ''

  return (
    <div className={`rounded-xl border border-l-4 p-4 shadow-sm ${dark ? 'border-neutral-800 bg-neutral-900/60' : 'border-slate-200 bg-white'} ${typeColor.card}`}>
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className={`text-sm font-bold tracking-tight ${typeColor.label}`}>{room.display_label}</span>
          {room.is_self_added && <Badge tone={dark ? 'gold' : 'blue'}>Added by company</Badge>}
        </div>
        <div className="flex items-center gap-2">
          <OccupancyBadge status={status} />
          {room.is_self_added && isEmptyRoom && onRemoveRoom && (
            <Button
              size="sm"
              variant="ghost"
              className={`h-6 px-2 text-xs ${dark ? '!text-red-400 hover:!bg-red-950/40' : 'text-red-600'}`}
              disabled={roomActionBusy}
              onClick={removeRoom}
            >
              Remove room
            </Button>
          )}
        </div>
      </div>
      {(showPhysicalRoomNumbers || isAdmin) &&
        (canEditDetails ? (
          <div className={`mb-3 rounded-md p-2.5 ${dark ? 'bg-neutral-800/70' : 'bg-slate-50'}`}>
            <div className="flex flex-wrap items-end gap-2.5">
              <div>
                <label className={`mb-1 block text-[10px] font-medium uppercase tracking-wide ${dark ? 'text-neutral-500' : 'text-slate-400'}`}>Physical room</label>
                <Input
                  className={`h-8 w-[90px] px-2 py-1 text-xs ${inputDark}`}
                  value={draft.physical_room_number}
                  onChange={(e) => scheduleDetailsSave({ physical_room_number: e.target.value })}
                  placeholder="-"
                />
              </div>
              <div>
                <label className={`mb-1 block text-[10px] font-medium uppercase tracking-wide ${dark ? 'text-neutral-500' : 'text-slate-400'}`}>Check-in</label>
                <Input
                  type="date"
                  className={`h-8 px-2 py-1 text-xs ${inputDark}`}
                  value={draft.check_in}
                  onChange={(e) => scheduleDetailsSave({ check_in: e.target.value })}
                />
              </div>
              <div>
                <label className={`mb-1 block text-[10px] font-medium uppercase tracking-wide ${dark ? 'text-neutral-500' : 'text-slate-400'}`}>Check-out</label>
                <Input
                  type="date"
                  className={`h-8 px-2 py-1 text-xs ${inputDark}`}
                  value={draft.check_out}
                  onChange={(e) => scheduleDetailsSave({ check_out: e.target.value })}
                />
              </div>
              <div>
                <label className={`mb-1 block text-[10px] font-medium uppercase tracking-wide ${dark ? 'text-neutral-500' : 'text-slate-400'}`}>Board type</label>
                <Input
                  list={`board-type-suggestions-${room.id}`}
                  className={`h-8 w-[140px] px-2 py-1 text-xs ${inputDark}`}
                  value={draft.board_type}
                  onChange={(e) => scheduleDetailsSave({ board_type: e.target.value })}
                  placeholder="e.g. Half Board"
                />
                <datalist id={`board-type-suggestions-${room.id}`}>
                  {BOARD_TYPE_SUGGESTIONS.map((opt) => (
                    <option key={opt} value={opt} />
                  ))}
                </datalist>
              </div>
              {nights != null && (
                <Badge tone={dark ? 'gold' : 'blue'}>
                  {nights} night{nights === 1 ? '' : 's'}
                </Badge>
              )}
              <SaveStatus state={detailsSaveState} />
            </div>
          </div>
        ) : (
          hasAnyDetail && (
            <div className={`mb-3 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md px-2.5 py-1.5 text-xs ${dark ? 'bg-neutral-800/70 text-neutral-300' : 'bg-slate-50 text-slate-600'}`}>
              {room.physical_room_number && (
                <span>
                  <span className={dark ? 'text-neutral-500' : 'text-slate-400'}>Room:</span> <span className="font-medium">{room.physical_room_number}</span>
                </span>
              )}
              {room.check_in && (
                <span>
                  <span className={dark ? 'text-neutral-500' : 'text-slate-400'}>Check-in:</span> <span className="font-medium">{formatDateOnly(room.check_in)}</span>
                </span>
              )}
              {room.check_out && (
                <span>
                  <span className={dark ? 'text-neutral-500' : 'text-slate-400'}>Check-out:</span> <span className="font-medium">{formatDateOnly(room.check_out)}</span>
                </span>
              )}
              {nights != null && (
                <Badge tone={dark ? 'gold' : 'blue'}>
                  {nights} night{nights === 1 ? '' : 's'}
                </Badge>
              )}
              {room.board_type && <Badge tone={dark ? 'gold' : 'amber'}>{room.board_type}</Badge>}
            </div>
          )
        ))}
      <div className="space-y-3">
        {room.guests.map((guest, idx) => (
          <GuestSlot
            key={guest.id}
            guest={guest}
            slotLabel={`Guest ${idx + 1}`}
            canFill={canFill}
            canEdit={canEdit}
            canClear={canClear}
            canMove={canMove}
            readOnlyMessage={readOnlyMessage}
            dark={dark}
            allHotels={allHotels}
            onSaveGuest={onSaveGuest}
            onClearGuest={onClearGuest}
            onMoveGuest={onMoveGuest}
            onDataChanged={onDataChanged}
          />
        ))}
        {room.unlimited_capacity && onAddGuestSlot && (
          <button
            type="button"
            disabled={roomActionBusy}
            onClick={addGuestSlot}
            className={`w-full rounded-lg border border-dashed py-2 text-xs font-semibold ${
              dark
                ? 'border-amber-700/40 text-amber-300 hover:bg-amber-500/10'
                : 'border-slate-300 text-slate-500 hover:bg-slate-50'
            }`}
          >
            + Add another guest to this room
          </button>
        )}
      </div>
    </div>
  )
}

function GuestSlot({
  guest,
  slotLabel,
  canFill,
  canEdit,
  canClear,
  canMove,
  readOnlyMessage,
  dark = false,
  allHotels,
  onSaveGuest,
  onClearGuest,
  onMoveGuest,
  onDataChanged,
}: {
  guest: PortalGuest
  slotLabel: string
  canFill: boolean
  canEdit: boolean
  canClear: boolean
  canMove: boolean
  readOnlyMessage?: string
  dark?: boolean
  allHotels: PortalHotel[]
  onSaveGuest: RoomTreeProps['onSaveGuest']
  onClearGuest: RoomTreeProps['onClearGuest']
  onMoveGuest: RoomTreeProps['onMoveGuest']
  onDataChanged: () => void
}) {
  const isFilled = !!guest.full_name
  const [name, setName] = useState(guest.full_name || '')
  const [mobile, setMobile] = useState(guest.mobile || '')
  const [updatedAt, setUpdatedAt] = useState(guest.updated_at)
  const [saveState, setSaveState] = useState<SaveState>('idle')
  const [errorMsg, setErrorMsg] = useState<string | undefined>()
  const [duplicateInfo, setDuplicateInfo] = useState<any>(null)
  const [showMove, setShowMove] = useState(false)
  const [isEditingArmed, setIsEditingArmed] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout>>()
  const { push } = useToast()

  useEffect(() => {
    setName(guest.full_name || '')
    setMobile(guest.mobile || '')
    setUpdatedAt(guest.updated_at)
  }, [guest.id, guest.full_name, guest.mobile, guest.updated_at])

  // Only reset the "armed for editing" state when this slot becomes a different
  // guest entirely (e.g. after a Move). Deliberately NOT keyed on updated_at —
  // that changes right after every autosave, and re-locking then would yank the
  // field shut while the person is still mid-edit.
  useEffect(() => {
    setIsEditingArmed(false)
  }, [guest.id])

  const editable = isFilled ? canEdit : canFill
  const disabled = !editable
  const showLocked = isFilled && !isEditingArmed

  const doSave = useCallback(
    async (nextName: string, nextMobile: string) => {
      setSaveState('saving')
      setDuplicateInfo(null)
      const res = await onSaveGuest(guest.id, { full_name: nextName, mobile: nextMobile }, updatedAt)
      if (res.ok) {
        setSaveState('saved')
        if (res.updated) setUpdatedAt(res.updated.updated_at)
        onDataChanged()
      } else {
        setSaveState('error')
        if (res.code === 'duplicate_mobile') {
          const info = tryParseJson<any>(res.detail || null)
          setDuplicateInfo(info)
          setErrorMsg('Duplicate mobile number')
        } else if (res.code === 'stale_write') {
          setErrorMsg('Modified elsewhere — refresh to see the latest version')
        } else {
          setErrorMsg(res.detail || res.code || 'Save failed')
        }
      }
    },
    [guest.id, onSaveGuest, onDataChanged, updatedAt]
  )

  function scheduleSave(nextName: string, nextMobile: string) {
    if (timer.current) clearTimeout(timer.current)
    // The database requires a guest slot to have BOTH a name and a mobile
    // number, or NEITHER -- never just one. Typing the name first (or the
    // mobile first) always passes through a half-filled state for a moment,
    // so don't fire a save while only one side is filled in: it would just
    // bounce off that rule with a confusing "mobile number required" error,
    // and since nothing gets saved yet anyway, wait quietly until both are
    // filled (or both are cleared) before attempting the write.
    const pairComplete = (nextName.trim() === '') === (nextMobile.trim() === '')
    if (!pairComplete) {
      setSaveState('idle')
      return
    }
    timer.current = setTimeout(() => doSave(nextName, nextMobile), 700)
  }

  const pairIncomplete = (name.trim() === '') !== (mobile.trim() === '')

  async function clear() {
    setSaveState('saving')
    const res = await onClearGuest(guest.id, updatedAt)
    if (res.ok) {
      setName('')
      setMobile('')
      setSaveState('idle')
      setIsEditingArmed(false)
      if (res.updated) setUpdatedAt(res.updated.updated_at)
      onDataChanged()
      push('Guest removed')
    } else {
      setSaveState('error')
      setErrorMsg(res.detail || res.code)
    }
  }

  const emptySlots: { hotelName: string; roomLabel: string; guestId: string }[] = []
  for (const h of allHotels) {
    for (const rt of h.room_types) {
      for (const r of rt.rooms) {
        for (const g of r.guests) {
          if (!g.full_name && g.id !== guest.id) {
            emptySlots.push({ hotelName: h.name, roomLabel: r.display_label, guestId: g.id })
          }
        }
      }
    }
  }

  const slotBorderBg = dark
    ? isFilled
      ? 'border-neutral-700 bg-neutral-900'
      : 'border-dashed border-neutral-700 bg-neutral-900/30'
    : isFilled
    ? 'border-slate-200 bg-white'
    : 'border-dashed border-slate-200 bg-slate-50/50'

  return (
    <div className={`rounded-lg border p-3.5 transition-colors ${slotBorderBg}`}>
      <div className="mb-2.5 flex items-center justify-between gap-2">
        <span
          className={`text-xs font-semibold uppercase tracking-wide ${
            dark ? (isFilled ? 'text-neutral-400' : 'text-neutral-600') : isFilled ? 'text-slate-500' : 'text-slate-400'
          }`}
        >
          {slotLabel}
        </span>
        <div className="flex items-center gap-2">
          {isFilled && editable && (
            isEditingArmed ? (
              <button
                type="button"
                onClick={() => setIsEditingArmed(false)}
                className={
                  dark
                    ? 'rounded-md border border-neutral-700 bg-neutral-800 px-2.5 py-1 text-[11px] font-semibold text-neutral-300 hover:bg-neutral-700'
                    : 'rounded-md border border-slate-300 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-600 hover:bg-slate-100'
                }
              >
                Done
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setIsEditingArmed(true)}
                className={
                  dark
                    ? 'rounded-md border border-amber-600/40 bg-amber-500/10 px-2.5 py-1 text-[11px] font-semibold text-amber-300 hover:bg-amber-500/20'
                    : 'rounded-md border border-blue-200 bg-blue-50 px-2.5 py-1 text-[11px] font-semibold text-blue-700 hover:bg-blue-100'
                }
              >
                Change
              </button>
            )
          )}
          <SaveStatus state={saveState} errorMessage={errorMsg} />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label className={`mb-1 block text-[11px] font-medium uppercase tracking-wide ${dark ? 'text-neutral-500' : 'text-slate-400'}`}>Full Name</label>
          {showLocked ? (
            <div
              className={`flex h-12 items-center rounded-md border px-3.5 text-base font-medium ${
                dark ? 'border-neutral-700 bg-neutral-800/60 text-neutral-100' : 'border-slate-200 bg-slate-50 text-slate-800'
              }`}
            >
              {name || <span className={dark ? 'text-neutral-600' : 'text-slate-400'}>—</span>}
            </div>
          ) : (
            <Input
              placeholder="e.g. John Smith"
              value={name}
              disabled={disabled}
              autoFocus={isEditingArmed}
              onChange={(e) => {
                setName(e.target.value)
                scheduleSave(e.target.value, mobile)
              }}
              className={`h-12 px-3.5 text-base ${
                dark ? '!border-neutral-700 !bg-neutral-900 !text-neutral-100 placeholder:!text-neutral-600 focus:!border-amber-500 focus:!ring-amber-500' : ''
              }`}
            />
          )}
        </div>
        <div>
          <label className={`mb-1 block text-[11px] font-medium uppercase tracking-wide ${dark ? 'text-neutral-500' : 'text-slate-400'}`}>Mobile Number</label>
          {showLocked ? (
            <div
              className={`flex h-12 items-center rounded-md border px-3.5 text-base font-medium ${
                dark ? 'border-neutral-700 bg-neutral-800/60 text-neutral-100' : 'border-slate-200 bg-slate-50 text-slate-800'
              }`}
            >
              {mobile || <span className={dark ? 'text-neutral-600' : 'text-slate-400'}>—</span>}
            </div>
          ) : (
            <Input
              placeholder="e.g. 010xxxxxxxx"
              value={mobile}
              disabled={disabled}
              onChange={(e) => {
                setMobile(e.target.value)
                scheduleSave(name, e.target.value)
              }}
              className={`h-12 px-3.5 text-base ${
                dark ? '!border-neutral-700 !bg-neutral-900 !text-neutral-100 placeholder:!text-neutral-600 focus:!border-amber-500 focus:!ring-amber-500' : ''
              }`}
            />
          )}
        </div>
      </div>
      {isFilled && isEditingArmed && (
        <p className={`mt-2 text-xs ${dark ? 'text-neutral-500' : 'text-slate-400'}`}>Editing — changes save automatically. Click "Done" when finished.</p>
      )}
      {pairIncomplete && (
        <p className={`mt-2 text-xs ${dark ? 'text-amber-300' : 'text-amber-600'}`}>
          {name.trim() === '' ? 'Add a full name to save this guest.' : 'Add a mobile number to save this guest.'}
        </p>
      )}
      {duplicateInfo && (
        <p className={`mt-2 text-xs ${dark ? 'text-red-400' : 'text-red-600'}`}>
          This mobile number is already registered in this event — {duplicateInfo.full_name} ({duplicateInfo.company_name}, {duplicateInfo.hotel_name}, {duplicateInfo.room_label}).
        </p>
      )}
      {!editable && !isFilled && readOnlyMessage && <p className={`mt-2 text-xs ${dark ? 'text-neutral-500' : 'text-slate-400'}`}>{readOnlyMessage}</p>}
      {isFilled && (canClear || canMove) && (
        <div className={`mt-2.5 flex gap-1.5 border-t pt-2.5 ${dark ? 'border-neutral-800' : 'border-slate-100'}`}>
          {canMove && (
            <Button
              size="sm"
              variant="ghost"
              className={`h-6 px-2 text-xs ${dark ? '!text-neutral-300 hover:!bg-neutral-800' : ''}`}
              onClick={() => setShowMove(true)}
            >
              Move
            </Button>
          )}
          {canClear && (
            <Button
              size="sm"
              variant="ghost"
              className={`h-6 px-2 text-xs ${dark ? '!text-red-400 hover:!bg-red-950/40' : 'text-red-600'}`}
              onClick={clear}
            >
              Clear
            </Button>
          )}
        </div>
      )}

      {showMove && (
        <Modal open onClose={() => setShowMove(false)} title={`Move ${name || 'guest'} to…`} dark={dark}>
          {emptySlots.length === 0 ? (
            <p className={`text-sm ${dark ? 'text-neutral-500' : 'text-slate-500'}`}>No empty slots available to move into.</p>
          ) : (
            <div className="max-h-72 space-y-1 overflow-y-auto">
              {emptySlots.map((s) => (
                <button
                  key={s.guestId}
                  className={
                    dark
                      ? 'flex w-full items-center justify-between rounded border border-neutral-700 px-3 py-2 text-left text-sm text-neutral-200 hover:bg-neutral-800'
                      : 'flex w-full items-center justify-between rounded border border-slate-200 px-3 py-2 text-left text-sm hover:bg-slate-50'
                  }
                  onClick={async () => {
                    const res = await onMoveGuest(guest.id, s.guestId)
                    if (res.ok) {
                      push('Guest moved')
                      setShowMove(false)
                      onDataChanged()
                    } else {
                      push(res.detail || res.code || 'Move failed', 'error')
                    }
                  }}
                >
                  <span>
                    {s.hotelName} — {s.roomLabel}
                  </span>
                  <Badge tone="slate" className={dark ? '!bg-neutral-800 !text-neutral-300' : ''}>
                    Empty
                  </Badge>
                </button>
              ))}
            </div>
          )}
        </Modal>
      )}
    </div>
  )
}
